// Portfolio Administration v1.0 PR 6 — list page for the unified
// waterfall codes (WF1+WF2). Replaces the PR 0 ComingSoonPage at
// /portfolio-admin/waterfalls. Behind PortfolioAdminV1; falls back to
// the placeholder when the flag is off so rollouts stay reversible.

import { useFeatureFlag } from "@/hooks/portfolioAdmin/useFeatureFlag";
import { useWaterfallsSummary } from "@/hooks/portfolioAdmin/usePr6";
import { ComingSoonPage } from "@/components/ComingSoonPage";
import { WaterfallsList } from "@/components/portfolioAdmin/waterfalls/WaterfallsList";

export default function WaterfallsListPage() {
  const flagOn = useFeatureFlag("PortfolioAdminV1");
  if (!flagOn) {
    return (
      <ComingSoonPage
        feature="Waterfalls"
        pr="PR 6"
        description="Admin-only versioned editor for the unified waterfall configurations (WF1 retained-share, WF2 contributed-share). See spec §10."
        backTo="/portfolio-admin/loans"
        backLabel="Back to Loans"
      />
    );
  }
  return <WaterfallsListPageInner />;
}

// WF1-only consolidation (2026-07): WF2 (contributed-share) is deferred and
// has never run in production, so it is hidden from this list as a deliberate,
// temporary de-clutter pending the WF2 rollout later this year. This is a
// display filter ONLY — WF2's config rows, runtime rows, and engine paths are
// untouched, and its detail route stays deep-linkable. Remove this filter to
// restore WF2.
const HIDDEN_WATERFALL_CODES = new Set(["WF2"]);

function WaterfallsListPageInner() {
  const query = useWaterfallsSummary();
  const rows = (query.data ?? []).filter(
    (r) => !HIDDEN_WATERFALL_CODES.has(r.waterfallCode),
  );

  return (
    <div
      className="flex flex-col gap-space-4"
      data-testid="waterfalls-list-page"
    >
      <div className="flex flex-col gap-space-1">
        <h1 className="font-display font-bold text-h1 m-0">Waterfalls</h1>
        <p className="text-small text-gray-mid m-0">
          The unified waterfall codes (WF1 retained-share, WF2
          contributed-share) govern every dollar movement on Phoenix loans.
          Editing requires a propose-and-approve flow with legal sign-off
          before activation; past runs are pinned to the version — and the
          legacy waterfall code — that was active at run time.
        </p>
      </div>

      {query.isLoading ? (
        <p className="text-small text-gray-mid">Loading waterfalls…</p>
      ) : query.isError ? (
        <p
          className="text-small text-status-error"
          role="alert"
          data-testid="waterfalls-list-error"
        >
          Failed to load waterfall summary.
        </p>
      ) : (
        <WaterfallsList summaries={rows} />
      )}
    </div>
  );
}
